/**
 * [C38] Office backup key on paper and in shares (D-173, D-208, D-209; ADR-05 §2.2). Browser-safe: no Node imports,
 * so the office portal bundles this file (`@faylasuf/crypto/key-sheet`) to generate, print, split and check the key in
 * the Super Manager's browser — the platform never sees the private key (D-042).
 *
 * Text form (D-355): `kind ‖ payload ‖ CRC-16`, Crockford base32, groups of 4 separated by "-". Kinds: 1 = the full
 * 32-byte private key; 2 = one Shamir share (x ‖ 32 bytes). The checksum catches a mistyped group before any
 * cryptography runs. The same text is the QR content.
 *
 * Shamir secret sharing (D-208): byte-wise polynomials over GF(2^8) (AES field, x^8 + x^4 + x^3 + x + 1), threshold k,
 * shares at x = 1 … n. With k = 2 one share reveals nothing about the key: every key value is equally consistent with
 * it. The randomness comes from the caller (Web Crypto in the browser, `crypto.randomBytes` on the server).
 */

const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
(() => {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    // multiply by the generator 3 = x + 1
    x ^= (x << 1) ^ (x & 0x80 ? 0x11b : 0);
    x &= 0xff;
  }
  for (let i = 255; i < 512; i++) EXP[i] = EXP[i - 255]!;
})();

function gfMul(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  return EXP[LOG[a]! + LOG[b]!]!;
}
function gfDiv(a: number, b: number): number {
  if (b === 0) throw new Error("GF_DIV_ZERO");
  if (a === 0) return 0;
  return EXP[LOG[a]! + 255 - LOG[b]!]!;
}

export interface KeyShare {
  readonly x: number; // 1 … 255
  readonly y: Uint8Array;
}

/** Splits `secret` into `n` shares, any `k` of which rebuild it. */
export function splitSecret(secret: Uint8Array, k: number, n: number, random: (len: number) => Uint8Array): KeyShare[] {
  if (k < 2 || n < k || n > 255) throw new Error("SHAMIR_PARAMS");
  const coeffs = random(secret.length * (k - 1));
  if (coeffs.length !== secret.length * (k - 1)) throw new Error("SHAMIR_RANDOM");
  const shares: KeyShare[] = [];
  for (let x = 1; x <= n; x++) {
    const y = new Uint8Array(secret.length);
    for (let i = 0; i < secret.length; i++) {
      // Horner: a_{k-1} x^{k-1} + … + a_1 x + s
      let acc = 0;
      for (let j = k - 2; j >= 0; j--) acc = gfMul(acc ^ coeffs[i * (k - 1) + j]!, x);
      y[i] = acc ^ secret[i]!;
    }
    shares.push({ x, y });
  }
  coeffs.fill(0);
  return shares;
}

/** Rebuilds the secret from `k` (or more) distinct shares (Lagrange interpolation at 0). */
export function combineShares(shares: readonly KeyShare[]): Uint8Array {
  if (shares.length < 2) throw new Error("SHAMIR_TOO_FEW");
  const len = shares[0]!.y.length;
  const xs = new Set<number>();
  for (const s of shares) {
    if (s.y.length !== len || s.x < 1 || s.x > 255 || xs.has(s.x)) throw new Error("SHAMIR_SHARES_INVALID");
    xs.add(s.x);
  }
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    let acc = 0;
    for (const a of shares) {
      let basis = 1; // ∏ x_b / (x_b − x_a); subtraction is XOR in GF(2^8)
      for (const b of shares) if (b !== a) basis = gfMul(basis, gfDiv(b.x, b.x ^ a.x));
      acc ^= gfMul(a.y[i]!, basis);
    }
    out[i] = acc;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Text form
// ---------------------------------------------------------------------------------------------------------------

const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ"; // Crockford: no I, L, O, U

export const SHEET_KIND = { key: 1, share: 2 } as const;
export type SheetKind = keyof typeof SHEET_KIND;

/** CRC-16/CCITT-FALSE. */
export function crc16(data: Uint8Array): number {
  let crc = 0xffff;
  for (const b of data) {
    crc ^= b << 8;
    for (let i = 0; i < 8; i++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
  }
  return crc;
}

function base32(bytes: Uint8Array): string {
  let out = "";
  let bits = 0;
  let value = 0;
  for (const b of bytes) {
    value = (value << 8) | b;
    bits += 8;
    while (bits >= 5) {
      out += ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
    value &= (1 << bits) - 1;
  }
  if (bits > 0) out += ALPHABET[(value << (5 - bits)) & 31];
  return out;
}

function unbase32(text: string): Uint8Array {
  const out: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of text) {
    const v = ALPHABET.indexOf(ch);
    if (v < 0) throw new Error("SHEET_CHARACTER");
    value = (value << 5) | v;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 0xff);
      bits -= 8;
    }
    value &= (1 << bits) - 1;
  }
  return Uint8Array.from(out);
}

/** The printed text of a key or a share: `XXXX-XXXX-…`. */
export function encodeSheet(kind: SheetKind, payload: Uint8Array): string {
  const body = new Uint8Array(1 + payload.length + 2);
  body[0] = SHEET_KIND[kind];
  body.set(payload, 1);
  const crc = crc16(body.subarray(0, 1 + payload.length));
  body[body.length - 2] = crc >> 8;
  body[body.length - 1] = crc & 0xff;
  const t = base32(body);
  return t.match(/.{1,4}/g)!.join("-");
}

/**
 * Reads a typed or scanned text back. Tolerant of what people type: lower case, spaces, missing dashes, and the
 * Crockford look-alikes O → 0, I / L → 1. Throws SHEET_CHECKSUM when a group is wrong.
 */
export function decodeSheet(text: string): { kind: SheetKind; payload: Uint8Array } {
  const clean = text.toUpperCase().replace(/[\s-]/g, "").replace(/O/g, "0").replace(/[IL]/g, "1");
  const body = unbase32(clean);
  if (body.length < 4) throw new Error("SHEET_LENGTH");
  const n = body.length;
  const crc = crc16(body.subarray(0, n - 2));
  if (((crc >> 8) & 0xff) !== body[n - 2] || (crc & 0xff) !== body[n - 1]) throw new Error("SHEET_CHECKSUM");
  const kind = body[0] === 1 ? "key" : body[0] === 2 ? "share" : null;
  if (!kind) throw new Error("SHEET_KIND");
  const payload = body.slice(1, n - 2);
  if ((kind === "key" && payload.length !== 32) || (kind === "share" && payload.length !== 33)) throw new Error("SHEET_LENGTH");
  return { kind, payload };
}

export const shareToBytes = (s: KeyShare): Uint8Array => Uint8Array.from([s.x, ...s.y]);
export const shareFromBytes = (b: Uint8Array): KeyShare => {
  if (b.length !== 33 || b[0] === 0) throw new Error("SHARE_FORMAT");
  return { x: b[0]!, y: b.slice(1) };
};
